import { Flex, Text, Box } from '@chakra-ui/react';
import SortArrowIcon, { SortIconDirection } from '../../SortArrowIcon';

export type DashboardSortField = 
    | 'partsOrdered' 
    | 'daysActive' 
    | 'orderValue' 
    | 'grossMargin' 
    | 'deliveryTime'
    | 'partsToQuote'
    | 'partsQuoting'
    | 'timeOrdered'
    | null;

export type DashboardSortDirection = 'asc' | 'desc';

export interface SortState {
    field: DashboardSortField;
    direction: DashboardSortDirection;
}

interface SortableTableHeaderProps {
    label: string;
    field: DashboardSortField;
    sortState: SortState;
    onSort: (field: DashboardSortField) => void;
    justify?: 'flex-start' | 'center' | 'flex-end';
}

export const getSortDirection = (field: DashboardSortField, sortState: SortState): SortIconDirection => {
    if (sortState.field !== field) return 'none';
    return sortState.direction;
};

export const toggleSort = (
    currentState: SortState,
    field: DashboardSortField
): SortState => {
    // Cycle: none -> asc -> desc -> none
    if (currentState.field !== field) {
        return { field, direction: 'asc' };
    }
    
    if (currentState.direction === 'asc') {
        return { field, direction: 'desc' };
    }
    
    // Reset to none
    return { field: null, direction: 'asc' };
};

const SortableTableHeader = ({ 
    label, 
    field, 
    sortState, 
    onSort,
    justify = 'center'
}: SortableTableHeaderProps) => {
    const isActive = sortState.field === field;
    const direction = getSortDirection(field, sortState);

    return (
        <Flex
            as="button"
            type="button"
            align="center"
            justify={justify}
            gap="6px"
            w="100%"
            color={isActive ? "#005BC4" : "gray.500"}
            fontSize="11px"
            fontWeight="semibold"
            textTransform="uppercase"
            letterSpacing="wider"
            cursor="pointer"
            onClick={() => onSort(field)}
            _hover={{ color: "#005BC4" }}
            transition="color 0.2s"
        >
            <Text as="span">
                {label}
            </Text>
            <Box lineHeight={0} color={isActive ? "#005BC4" : "gray.400"}>
                <SortArrowIcon direction={direction} size={12} />
            </Box>
        </Flex>
    );
};

export default SortableTableHeader;
